/**
 * Client - View Certification Body's Accreditation Status
 * Clients can check if their CB is accredited
 */
import React from 'react';
import { BRAND } from '../config/branding';
import { useCanIssueCertificates } from '../hooks/useAccreditation';
import { LoadingSpinner, ErrorMessage } from '../components/AccreditationComponents';
import { formatDate } from '../api/accreditationService';

const ClientAccreditationView = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  // Assuming client's company has a parent_company_id which is their CB
  const cbCompanyId = user.parent_company_id || user.company_id;

  const { canIssue, loading, error } = useCanIssueCertificates(cbCompanyId);

  const containerStyle = {
    padding: '24px',
    maxWidth: '900px',
    margin: '0 auto'
  };

  const titleStyle = {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#333',
    marginBottom: '32px'
  };

  const cardStyle = {
    background: '#fff',
    padding: '32px',
    borderRadius: '12px',
    border: '1px solid #e0e0e0',
    marginBottom: '24px'
  };

  const statusBoxStyle = (isAccredited) => ({
    padding: '24px',
    background: isAccredited ? '#e8f5e9' : '#ffebee',
    border: `2px solid ${isAccredited ? '#4caf50' : '#f44336'}`,
    borderRadius: '8px',
    marginBottom: '24px'
  });

  const statusIconStyle = {
    fontSize: '3rem',
    marginBottom: '12px'
  };

  const statusTextStyle = {
    fontSize: '1.5rem',
    fontWeight: '600',
    marginBottom: '8px'
  };

  const infoGridStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginTop: '24px'
  };

  const infoItemStyle = {
    padding: '16px',
    background: '#f9f9f9',
    borderRadius: '8px'
  };

  const labelStyle = {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#999',
    marginBottom: '8px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  };

  const valueStyle = {
    fontSize: '1.125rem',
    fontWeight: '600',
    color: '#333'
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <div style={containerStyle}>
      <h1 style={titleStyle}>Certification Body Accreditation Status</h1>

      <div style={cardStyle}>
        {canIssue && canIssue.can_issue ? (
          <>
            <div style={statusBoxStyle(true)}>
              <div style={statusIconStyle}>--</div>
              <div style={statusTextStyle}>Accredited Certification Body</div>
              <div style={{ color: '#666' }}>
                Your certification body is accredited by ICSC and authorized to issue ISO certificates.
              </div>
            </div>

            <div style={infoGridStyle}>
              <div style={infoItemStyle}>
                <div style={labelStyle}>Accreditation Number</div>
                <div style={valueStyle}>{canIssue.accreditation_number}</div>
              </div>

              <div style={infoItemStyle}>
                <div style={labelStyle}>Valid Until</div>
                <div style={valueStyle}>{formatDate(canIssue.expiry_date)}</div>
              </div>
            </div>

            <div style={{ marginTop: '24px', padding: '16px', background: '#e3f2fd', borderRadius: '8px' }}>
              <strong>What this means for you:</strong>
              <ul style={{ marginTop: '8px', color: '#666' }}>
                <li>Your certificates are backed by ICSC accreditation</li>
                <li>Your certification body meets international standards</li>
                <li>Your certificates are recognized globally</li>
                <li>Regular surveillance ensures ongoing compliance</li>
              </ul>
            </div>
          </>
        ) : (
          <>
            <div style={statusBoxStyle(false)}>
              <div style={statusIconStyle}></div>
              <div style={statusTextStyle}>Not Accredited</div>
              <div style={{ color: '#666' }}>
                {canIssue?.reason || 'Your certification body is not currently accredited by ICSC.'}
              </div>
            </div>

            <div style={{ padding: '16px', background: '#fff3e0', borderRadius: '8px', border: '1px solid #ffb74d' }}>
              <strong>Important:</strong>
              <ul style={{ marginTop: '8px', color: '#666' }}>
                <li>Certificates may not be issued until accreditation is granted</li>
                <li>Contact your certification body for more information</li>
                <li>Accreditation ensures quality and global recognition</li>
              </ul>
            </div>
          </>
        )}
      </div>

      <div style={{ ...cardStyle, background: '#f9f9f9' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '16px' }}>
          About {BRAND.org}
        </h2>
        <p style={{ color: '#666', lineHeight: '1.6', marginBottom: '12px' }}>
          {BRAND.short} (International Certification Standards Council) is an accreditation body that ensures certification bodies meet international standards for quality and competence.
        </p>
        <p style={{ color: '#666', lineHeight: '1.6' }}>
          When your certification body is ICSC accredited, it means they have undergone rigorous assessment of their processes, auditors, and quality management systems, and are subject to regular surveillance to maintain high standards.
        </p>
      </div>
    </div>
  );
};

export default ClientAccreditationView;
